﻿import React, { useState, useEffect } from 'react';
import { CrudFunctions } from '../../utilities/crud-functions';
import GlobalDialog from '../Modal/GlobalDialog';
import { ModalMetadata } from '../../models/modal-metadata';
import { BenchSheetTechnologyType, ModalMessageType } from '../../models/enums';
import ColumnSearchComponent from '../Common/ColumnSearchComponent';
import { MethodColumnRequest } from '../../models/Methods/method-column-request';
import { ColumnHeader } from '../../models/column-header';
import { ItemBasicInformation } from '../../models/item-basic-information';
import ItemBasicInformationDialog from '../ItemBasicInformationDialog';

import DataGrid, {
    Column,
    Selection,
    Button as ColumnButton,
    Toolbar,
    Item as ToolbarItem,
    Editing,
    Paging,
    LoadPanel    
} from 'devextreme-react/data-grid';

import Button from 'devextreme-react/button';
import { Popup as DialogPopup } from 'devextreme-react/popup';

let key = 0;

export interface Props {
    methodId: string;
    technologyId?: BenchSheetTechnologyType;
    readOnly: boolean;
}

export default function MethodColumnComponent(props: Props) {
    const [columns, setColumns] =
        useState<ColumnHeader[]>();

    const [columnSearchDialogVisible, setColumnSearchDialogVisible] =
        useState<boolean>();

    const [itemBasicInformationDialogVisible, setItemBasicInformationDialogVisible] =
        useState<boolean>();

    const [itemBasicInformation, setItemBasicInformation] =
        useState<ItemBasicInformation>();

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>();

    useEffect(() => {
        getAllData();
    }, [props.methodId]);

    key++;

    return (
        <div>
            <div>
                <DataGrid height="10rem" width="100%"
                    dataSource={columns ? columns : []}
                    wordWrapEnabled={true}
                    allowColumnResizing={true}
                    columnResizingMode='widget'
                    keyExpr="columnHeaderId"
                    rowAlternationEnabled={true}
                    onRowRemoving={onColumnRemoving}
                    showRowLines={true}
                    showBorders={true}>

                    <LoadPanel enabled={true} />
                    <Paging enabled={false} />
                    <Selection mode='single' />
                    
                    <Column type="buttons" width="6rem">
                        <ColumnButton hint="information" icon="info"
                            onClick={onColumnInformationButtonClicked} />
                        <ColumnButton name="delete" hint="remove column" />
                    </Column>

                    <Column dataField='columnId' caption="Column ID" width='7rem' />
                    <Column dataField='vendorName' caption="Vendor" width='10rem' />
                    <Column dataField='manufacturerName' caption="Manufacturer" width='10rem' />
                    <Column dataField='dimensions' width='20rem' />
                    <Column dataField='vendorPartNumber' caption="Vendor Part#" width='9rem' />
                    <Column dataField='description' />

                    <Toolbar>
                        <ToolbarItem location='before'>
                            <div className="pw-datagrid-toolbar-template">
                                <div className="pw-datagrid-toobar-title">Columns</div>
                            </div>
                        </ToolbarItem>

                        <ToolbarItem location='after'>
                            <Button type="normal" icon="plus" hint="add a column"
                                onClick={showColumnSearchDialog} />
                        </ToolbarItem>
                    </Toolbar>

                    <Editing mode="row"
                        useIcons={true}
                        allowAdding={false}
                        allowUpdating={false}
                        allowDeleting={true} />
                </DataGrid>
            </div>

            {columnSearchDialogVisible &&
                <DialogPopup visible={columnSearchDialogVisible}
                    onHiding={hideColumnSearchDialog}
                    title="Column Search"
                    fullScreen={true}>
                    <ColumnSearchComponent
                        onApplySelection={onApplyColumnSearchClick}
                        onCancel={onCancelColumnSearchClick} />
                </DialogPopup>
            }

            {itemBasicInformationDialogVisible &&
                <DialogPopup visible={itemBasicInformationDialogVisible}
                    onHiding={hideItemBasicInformationDialog}
                    title="Item Basic Information"
                    width='25rem'
                    height='21rem'
                    dragEnabled={false}
                    resizeEnabled={false}>
                    <ItemBasicInformationDialog initInformation={itemBasicInformation}
                        onOkClick={hideItemBasicInformationDialog}
                        key={`IBI${key}`} />
                </DialogPopup>
            }

            {globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata} onClose={clearGlobalDialog} />
            }

        </div>
    );

    async function onApplyColumnSearchClick(columnHeader: ColumnHeader) {
        if (columnHeader) {
            let request: MethodColumnRequest = {
                methodId: props.methodId,
                columnHeaderId: columnHeader.columnHeaderId
            };

            await saveColumn(request)
                .then(() => {
                    getMethodColumns();
                })
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Saving Column");
                });
        }

        hideColumnSearchDialog();
    }

    function onCancelColumnSearchClick() {
        hideColumnSearchDialog();
    }

    async function onColumnRemoving(e: any) {
        if (e.data && e.data.columnHeaderId) {
            await removeColumn(e.data.columnHeaderId)
                .then(data => {
                    getMethodColumns();
                })
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Removing Column");
                });
        }
    }

    async function getMethodColumns() {
        await CrudFunctions.getData(`methods/methodcolumns/${props.methodId}/${props.technologyId}`)
            .then(data => {
                setColumns(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Method Columns");
            });
    }

    async function saveColumn(request: MethodColumnRequest) {
        return await CrudFunctions.postData("methods/column", request);
    }

    async function removeColumn(columnHeaderId: string) {
        return await CrudFunctions.deleteData(`methods/column/${props.methodId}/${columnHeaderId}`)
    }

    async function getAllData() {
        await Promise.all([
            getMethodColumns()
        ]);
    }

    function showColumnSearchDialog() {
        setColumnSearchDialogVisible(true);
    }

    function hideColumnSearchDialog() {
        setColumnSearchDialogVisible(false);
    }

    function onColumnInformationButtonClicked(e: any) {
        if (e.row && e.row.data) {
            const columnHeader = e.row.data as ColumnHeader;

            if (columnHeader) {
                let itemInformation: ItemBasicInformation = {
                    rowPointer: columnHeader.columnHeaderId,
                    createTimestamp: columnHeader.createTimestamp,
                    lastModifiedTimestamp: columnHeader.modifyTimestamp,
                    createUser: columnHeader.createUser,
                    modifyUser: columnHeader.modifyUser
                };

                setItemBasicInformation(itemInformation);
                setItemBasicInformationDialogVisible(true);
            }
        }
    }

    function hideItemBasicInformationDialog() {
        setItemBasicInformationDialogVisible(false);
    }

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }

    function clearGlobalDialog() {
        setGlobalDialogMetadata(null);
        setGlobalDialogVisible(false);
    }

}

